import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  where,
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
// import { useAuth } from '../context/AuthContext';

function AdminTransactionsPage() {
  // const { currentUser } = useAuth();
  const [journeyTransactions, setJourneyTransactions] = useState([]);
  const [surchargeTransactions, setSurchargeTransactions] = useState([]);
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, journey, surcharge, disbursement
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [modeFilter, setModeFilter] = useState('all'); // all, test, live
  const [searchTerm, setSearchTerm] = useState('');
  const [showDisbursementForm, setShowDisbursementForm] = useState(false);
  const [totals, setTotals] = useState({
    journeyTotal: 0,
    surchargeTotal: 0,
    disbursementTotal: 0,
    journeyCount: 0,
    surchargeCount: 0,
    disbursementCount: 0
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch journey transactions from userTransactions collection
      let journeyData = [];
      try {
        const journeyQuery = query(
          collection(db, 'userTransactions'),
          orderBy('purchaseDate', 'desc'),
          limit(500)
        );
        
        const journeySnapshot = await getDocs(journeyQuery);
        journeyData = journeySnapshot.docs.map(doc => ({
          id: doc.id,
          type: 'journey',
          ...doc.data(),
          purchaseDate: doc.data().purchaseDate?.toDate?.() || new Date()
        }));
      } catch (error) {
        console.log('Error fetching journey transactions:', error);
        // Try without orderBy if index is missing
        const simpleQuery = query(
          collection(db, 'userTransactions'),
          limit(500)
        );
        const snapshot = await getDocs(simpleQuery);
        journeyData = snapshot.docs.map(doc => ({
          id: doc.id,
          type: 'journey',
          ...doc.data(),
          purchaseDate: doc.data().purchaseDate?.toDate?.() || new Date()
        })).sort((a, b) => b.purchaseDate - a.purchaseDate);
      }

      // Fetch surcharge transactions from transactions collection
      // Try with composite index first, fall back to simple query if index doesn't exist
      let surchargeData = [];
      try {
        const surchargeQuery = query(
          collection(db, 'transactions'),
          where('type', '==', 'surcharge'),
          orderBy('createdAt', 'desc'),
          limit(500)
        );
        
        const surchargeSnapshot = await getDocs(surchargeQuery);
        surchargeData = surchargeSnapshot.docs.map(doc => ({
          id: doc.id,
          type: 'surcharge',
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
      } catch (indexError) {
        console.log('Composite index not available, trying simple query:', indexError.message);
        
        // Fallback: get all transactions and filter/sort in memory
        try {
          const allTransactionsQuery = query(
            collection(db, 'transactions'),
            limit(1000)
          );
          
          const allSnapshot = await getDocs(allTransactionsQuery);
          const allData = allSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date()
          }));
          
          // Filter for surcharge type and sort by date
          surchargeData = allData
            .filter(t => t.type === 'surcharge')
            .sort((a, b) => b.createdAt - a.createdAt)
            .slice(0, 500);
            
          console.log(`Found ${surchargeData.length} surcharge transactions`);
        } catch (fallbackError) {
          console.error('Error fetching transactions:', fallbackError);
        }
      }

      setJourneyTransactions(journeyData);
      setSurchargeTransactions(surchargeData);

      // Log for debugging
      console.log('Journey transactions:', journeyData.length);
      console.log('Surcharge transactions:', surchargeData.length);
      if (surchargeData.length > 0) {
        console.log('Sample surcharge transaction:', surchargeData[0]);
      }
      
      // Debug: Try to fetch ALL documents from transactions collection
      try {
        const allTransQuery = query(collection(db, 'transactions'));
        const allTransSnapshot = await getDocs(allTransQuery);
        console.log('Total documents in transactions collection:', allTransSnapshot.size);
        
        if (allTransSnapshot.size > 0) {
          console.log('First few transaction documents:');
          allTransSnapshot.docs.slice(0, 3).forEach(doc => {
            console.log('Doc ID:', doc.id, 'Data:', doc.data());
          });
        }
      } catch (debugError) {
        console.error('Debug query error:', debugError);
      }

      // Fetch disbursements
      let disbursementData = [];
      try {
        const disbursementQuery = query(
          collection(db, 'disbursements'),
          orderBy('createdAt', 'desc'),
          limit(500)
        );
        
        const disbursementSnapshot = await getDocs(disbursementQuery);
        disbursementData = disbursementSnapshot.docs.map(doc => ({
          id: doc.id,
          type: 'disbursement',
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
      } catch (error) {
        console.log('Error fetching disbursements, trying simple query:', error);
        // Fallback without orderBy
        try {
          const simpleQuery = query(collection(db, 'disbursements'), limit(500));
          const snapshot = await getDocs(simpleQuery);
          disbursementData = snapshot.docs.map(doc => ({
            id: doc.id,
            type: 'disbursement',
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date()
          })).sort((a, b) => b.createdAt - a.createdAt);
        } catch (fallbackError) {
          console.error('Error fetching disbursements:', fallbackError);
        }
      }
      
      setDisbursements(disbursementData);
      console.log('Disbursements:', disbursementData.length);

      // Calculate LIVE only totals (excluding test mode)
      const journeyLiveData = journeyData.filter(t => t.stripeMode !== 'test');
      const surchargeLiveData = surchargeData.filter(t => t.stripeMode !== 'test');
      
      const journeyLiveTotal = journeyLiveData.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      const surchargeLiveTotal = surchargeLiveData.reduce((sum, t) => sum + (t.amountUSD || 0), 0);
      const disbursementTotal = disbursementData.reduce((sum, t) => sum + (t.amountUSD || 0), 0);
      
      // Calculate test totals separately for the breakdown
      const journeyTestTotal = journeyData.filter(t => t.stripeMode === 'test').reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      const surchargeTestTotal = surchargeData.filter(t => t.stripeMode === 'test').reduce((sum, t) => sum + (t.amountUSD || 0), 0);
      
      // Count live transactions only
      const journeyLiveCount = journeyLiveData.length;
      const surchargeLiveCount = surchargeLiveData.length;
      
      setTotals({
        journeyTotal: journeyLiveTotal,  // Only live revenue
        surchargeTotal: surchargeLiveTotal,  // Only live revenue
        disbursementTotal,
        journeyCount: journeyLiveCount,  // Only live count
        surchargeCount: surchargeLiveCount,  // Only live count
        disbursementCount: disbursementData.length,
        testTotal: journeyTestTotal + surchargeTestTotal,
        liveTotal: journeyLiveTotal + surchargeLiveTotal,
        // Keep full counts for reference
        journeyTotalCount: journeyData.length,
        surchargeTotalCount: surchargeData.length
      });

    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let allTransactions = [];
    
    if (filter === 'all' || filter === 'journey') {
      allTransactions = [...allTransactions, ...journeyTransactions];
    }
    
    if (filter === 'all' || filter === 'surcharge') {
      allTransactions = [...allTransactions, ...surchargeTransactions];
    }
    
    if (filter === 'all' || filter === 'disbursement') {
      allTransactions = [...allTransactions, ...disbursements];
    }

    // Apply mode filter
    if (modeFilter !== 'all') {
      allTransactions = allTransactions.filter(t => {
        if (modeFilter === 'test') {
          return t.stripeMode === 'test';
        } else if (modeFilter === 'live') {
          return t.stripeMode === 'live';
        }
        return true;
      });
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch(dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      allTransactions = allTransactions.filter(t => {
        const tDate = t.purchaseDate || t.createdAt;
        return tDate >= filterDate;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      allTransactions = allTransactions.filter(t => 
        t.userEmail?.toLowerCase().includes(search) ||
        t.customerEmail?.toLowerCase().includes(search) ||
        t.routeName?.toLowerCase().includes(search) ||
        t.journeyId?.toLowerCase().includes(search) ||
        t.stripeSessionId?.toLowerCase().includes(search)
      );
    }

    // Sort by date
    allTransactions.sort((a, b) => {
      const dateA = a.purchaseDate || a.createdAt;
      const dateB = b.purchaseDate || b.createdAt;
      return dateB - dateA;
    });

    return allTransactions;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      completed: '#28a745',
      pending: '#ffc107',
      failed: '#dc3545',
      refunded: '#6c757d'
    };
    
    return (
      <span 
        style={{
          backgroundColor: statusColors[status] || '#6c757d',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      >
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      journey: '#007bff',
      surcharge: '#ff6b6b',
      disbursement: '#28a745'
    };
    
    return (
      <span 
        style={{
          backgroundColor: typeColors[type] || '#6c757d',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      >
        {type?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const getModeBadge = (mode) => {
    if (!mode || mode === 'N/A') {
      return <span style={{ color: '#999', fontSize: '12px' }}>—</span>;
    }
    
    const isTest = mode === 'test';
    return (
      <span 
        style={{
          backgroundColor: isTest ? '#ffc107' : '#28a745',
          color: isTest ? '#000' : 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 'bold',
          textTransform: 'uppercase'
        }}
      >
        {isTest ? '🧪 TEST' : '✓ LIVE'}
      </span>
    );
  };

  if (loading) {
    return <div className="admin-loading">Loading transactions...</div>;
  }

  const filteredTransactions = filterTransactions();

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Transaction History</h1>
        <button 
          onClick={fetchTransactions} 
          className="admin-refresh-btn"
          style={{ marginLeft: 'auto' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="admin-stats-grid" style={{ marginBottom: '30px' }}>
        <div className="admin-stat-card">
          <h3>Journey Purchases <span style={{ fontSize: '12px', color: '#28a745' }}>LIVE</span></h3>
          <p className="stat-value">
            {totals.journeyCount}
            {totals.journeyTotalCount - totals.journeyCount > 0 && (
              <span style={{ fontSize: '14px', color: '#999', marginLeft: '5px' }}>
                (+{totals.journeyTotalCount - totals.journeyCount} test)
              </span>
            )}
          </p>
          <p className="stat-label">{formatCurrency(totals.journeyTotal)}</p>
        </div>
        <div className="admin-stat-card">
          <h3>Surcharge Payments <span style={{ fontSize: '12px', color: '#28a745' }}>LIVE</span></h3>
          <p className="stat-value">
            {totals.surchargeCount}
            {totals.surchargeTotalCount - totals.surchargeCount > 0 && (
              <span style={{ fontSize: '14px', color: '#999', marginLeft: '5px' }}>
                (+{totals.surchargeTotalCount - totals.surchargeCount} test)
              </span>
            )}
          </p>
          <p className="stat-label">{formatCurrency(totals.surchargeTotal)}</p>
        </div>
        <div className="admin-stat-card">
          <h3>Disbursements</h3>
          <p className="stat-value">{totals.disbursementCount}</p>
          <p className="stat-label" style={{ color: '#dc3545' }}>-{formatCurrency(totals.disbursementTotal)}</p>
        </div>
        <div className="admin-stat-card">
          <h3>Net Revenue <span style={{ fontSize: '12px', color: '#28a745' }}>LIVE</span></h3>
          <p className="stat-value">{formatCurrency(totals.journeyTotal + totals.surchargeTotal - totals.disbursementTotal)}</p>
          <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
            <div>🧪 Test Revenue: {formatCurrency(totals.testTotal)}</div>
            <div>✓ Live Revenue: {formatCurrency(totals.liveTotal)}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-controls" style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => setShowDisbursementForm(true)}
          className="admin-btn admin-btn-primary"
          style={{ marginRight: '10px' }}
        >
          ➕ Add Disbursement
        </button>
        
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="admin-select"
        >
          <option value="all">All Transactions</option>
          <option value="journey">Journey Purchases</option>
          <option value="surcharge">Surcharge Payments</option>
          <option value="disbursement">Disbursements</option>
        </select>

        <select 
          value={modeFilter} 
          onChange={(e) => setModeFilter(e.target.value)}
          className="admin-select"
        >
          <option value="all">All Modes</option>
          <option value="test">Test Only</option>
          <option value="live">Live Only</option>
        </select>

        <select 
          value={dateFilter} 
          onChange={(e) => setDateFilter(e.target.value)}
          className="admin-select"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </select>

        <input
          type="text"
          placeholder="Search by email, journey ID, or route..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search"
          style={{ flex: 1 }}
        />
      </div>

      {/* Transactions Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Mode</th>
              <th>Customer</th>
              <th>Details</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Journey ID</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                  No transactions found
                </td>
              </tr>
            ) : (
              filteredTransactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{formatDate(transaction.purchaseDate || transaction.createdAt)}</td>
                  <td>{getTypeBadge(transaction.type)}</td>
                  <td>{getModeBadge(transaction.stripeMode)}</td>
                  <td>
                    <div>
                      <div>{transaction.userEmail || transaction.customerEmail || 'N/A'}</div>
                      {transaction.userName && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {transaction.userName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {transaction.type === 'journey' ? (
                      <div>
                        <div>{transaction.routeName || 'Unknown Route'}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {transaction.dateRange || `${transaction.nights || 0} nights`}
                        </div>
                      </div>
                    ) : transaction.type === 'surcharge' ? (
                      <div>
                        <div>Surcharge Payment</div>
                        {transaction.breakdown && transaction.breakdown.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {transaction.breakdown.length} room(s)
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div>{transaction.description || 'Manual Disbursement'}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {transaction.method || 'Manual'} - {transaction.reference || 'No reference'}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <div>
                      {formatCurrency(
                        transaction.totalAmount || transaction.amount || transaction.amountUSD,
                        transaction.currency
                      )}
                    </div>
                    {transaction.currency && transaction.currency !== 'USD' && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatCurrency(transaction.amountUSD || transaction.totalAmount)} USD
                      </div>
                    )}
                  </td>
                  <td>{getStatusBadge(transaction.status)}</td>
                  <td>
                    <a 
                      href={`#/admin/journeys/${transaction.journeyId}`}
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      {transaction.journeyId ? 
                        transaction.journeyId.substring(0, 8) + '...' : 
                        'N/A'
                      }
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Export Button */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button 
          onClick={() => {
            const csv = convertToCSV(filteredTransactions);
            downloadCSV(csv, 'transactions.csv');
          }}
          className="admin-btn admin-btn-secondary"
        >
          📥 Export to CSV
        </button>
      </div>

      {/* Disbursement Form Modal */}
      {showDisbursementForm && (
        <DisbursementForm 
          onClose={() => setShowDisbursementForm(false)}
          onSuccess={() => {
            setShowDisbursementForm(false);
            fetchTransactions();
          }}
        />
      )}
    </div>
  );
}

// Helper functions for CSV export
function convertToCSV(transactions) {
  const headers = ['Date', 'Type', 'Customer Email', 'Customer Name', 'Details', 'Amount (USD)', 'Currency', 'Status', 'Journey ID', 'Reference'];
  
  const rows = transactions.map(t => [
    new Date(t.purchaseDate || t.createdAt).toISOString(),
    t.type,
    t.userEmail || t.customerEmail || '',
    t.userName || t.customerName || '',
    t.type === 'journey' ? t.routeName : 
      t.type === 'surcharge' ? 'Surcharge Payment' : 
      t.description || 'Manual Disbursement',
    t.type === 'disbursement' ? -Math.abs(t.amountUSD || t.totalAmount || t.amount || 0) : 
      (t.amountUSD || t.totalAmount || t.amount || 0),
    t.currency || 'USD',
    t.status || '',
    t.journeyId || '',
    t.reference || ''
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  return csvContent;
}

function downloadCSV(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Disbursement Form Component
function DisbursementForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    customerEmail: '',
    customerName: '',
    amount: '',
    currency: 'USD',
    description: '',
    method: 'Manual Transfer',
    reference: '',
    journeyId: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerEmail || !formData.amount) {
      alert('Please fill in customer email and amount');
      return;
    }

    setLoading(true);
    try {
      const disbursementData = {
        ...formData,
        amount: parseFloat(formData.amount),
        amountUSD: formData.currency === 'USD' ? parseFloat(formData.amount) : parseFloat(formData.amount), // TODO: Add currency conversion
        type: 'disbursement',
        status: 'completed',
        createdAt: serverTimestamp(),
        createdBy: 'admin', // You can add currentUser.email here if needed
        timestamp: new Date().toISOString()
      };

      await addDoc(collection(db, 'disbursements'), disbursementData);
      alert('Disbursement recorded successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding disbursement:', error);
      alert('Failed to add disbursement: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>Add Manual Disbursement</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Customer Email *
            </label>
            <input
              type="email"
              value={formData.customerEmail}
              onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Customer Name
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Amount *
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                required
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                style={{
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Payment Method
            </label>
            <select
              value={formData.method}
              onChange={(e) => setFormData({ ...formData, method: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="Manual Transfer">Manual Transfer</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="PayPal">PayPal</option>
              <option value="Venmo">Venmo</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Reference/Transaction ID
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              placeholder="e.g., Bank transfer reference, PayPal transaction ID"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Journey ID (if applicable)
            </label>
            <input
              type="text"
              value={formData.journeyId}
              onChange={(e) => setFormData({ ...formData, journeyId: e.target.value })}
              placeholder="Link to specific journey"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Refund for cancelled booking, Cash balance payout"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Internal Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes for internal reference"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '60px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              className="admin-btn admin-btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={loading}
            >
              {loading ? 'Recording...' : 'Record Disbursement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminTransactionsPage;